import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import { toastr } from "react-redux-toastr";
import { Address } from "../../../core/api/models";
import { State } from "../../../core/store/interfaces";
import api from "../../../api";
import {
  useLead,
  capitalizeFirstLetter,
  AppPermissions,
  useHasPermission,
  Company,
  UpdateCompanyData,
  convertPascalCaseToWords,
} from "../../../core";

import { CompanyAvatar } from "../../../components/render-file/CompanyAvatar";
import EditCompany from "./company/EditCompany";
import EditCompanyPhone from "./company/EditCompanyPhone";
import EditCompanyEmail from "./company/EditCompanyEmail";
import EditCompanyLogo from "./company/EditCompanyLogo";
import AddCompanyAddress from "./company/AddCompanyAddress";
import { RouteKeys } from "../../routes/route-keys";
import AddressDetalsView from "./AddressDetalsView";

const CompanyDetails: React.FunctionComponent<{
  error?: string;
  setStage?: (stage: string) => void;
  updateCompany: (details: Company) => void;
  details?: Company;
  show360Link?: boolean;
  showDetailsLink?: boolean;
  stage?: string;
  onStateChange?: () => void;
}> = (props) => {
  const history = useHistory();

  const canUpdateCustomerContacts = useHasPermission(AppPermissions.UpdateCustomerContacts);

  const canViewCustomerAddress = useHasPermission(AppPermissions.ViewCustomerAddress);

  const leadState = useSelector((state: State) => state.lead);

  const [showDetails, setShowDetails] = useState(
    props.stage === "add-user" || props.stage === "lead-details" || props.stage === "edit"
      ? true
      : false
  );

  const [addressToDelete, setAddressToDelete] = useState<Address>();

  const [editModal, setEditModal] = React.useState<{
    isEditLogo?: boolean;
    isEditName?: boolean;
    isEditPhone?: boolean;
    isEditEmail?: boolean;
    isWebsiteUrl?: boolean;
    isEditIndustry?: boolean;
    isEditCinNumber?: boolean;
    isEditGstinNumber?: boolean;
    isEditSector?: boolean;
    isEditCompanySegment?: boolean;
    isShowAddressForm?: boolean;
  }>({});
  const [isShowMore, setShowMore] = useState<boolean>(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isShowSetExpectedCloseDate, setShowSetExpectedCloseDate] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<Address>();

  const selectAddress = (address?: Address) => {
    setSelectedAddress(address);
    setEditModal({ isShowAddressForm: true });
  };

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = (event: { target: any }) => {
    if (
      wrapperRef &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target) &&
      !event.target.className?.includes("react-datepicker__day")
    ) {
      setShowSetExpectedCloseDate(false);
    }
  };

  const loadSector = async () => {
    const response = await api.sector.loadAllSector();
    return response;
  };

  const loadIndustry = async () => {
    const response = await api.industry.loadAllIndustry();
    return response;
  };

  const loadCompanySegment = async () => {
    const response = await api.companySegment.loadAllCompanySegment();
    return response;
  };

  const onCloseDeleteConfirm = () => {
    setAddressToDelete(undefined);
  };

  const onRemoveAddress = async () => {
    let addresses = props.details && props.details?.addresses ? props.details?.addresses : [];
    if (addressToDelete) {
      addresses = addresses.filter((x) => x._id !== addressToDelete?._id);
      const updateData: UpdateCompanyData = {
        name: props.details?.name,
        cinNumber: props.details?.cinNumber,
        sector: props.details?.sector?._id,
        industry: props.details?.industry?._id,
        companySegment: props.details?.companySegment?._id,
        addresses,
      };
      try {
        const response = await api.company.updateCompnay(updateData, props.details?._id);
        setAddressToDelete(undefined);
        props.updateCompany(response);
      } catch (error) {
        if (error?.response?.data?.data?.message) {
          toastr.error("Error", error?.response?.data?.data?.message.join());
        } else {
          toastr.error("Error", error?.response?.data?.message);
        }
      }
    }
  };

  const linkedin = props.details?.socialMediaIds?.find((x) => x.name.toLowerCase() === "linkedin");

  const twitter = props.details?.socialMediaIds?.find((x) => x.name.toLowerCase() === "twitter");

  const instagram = props.details?.socialMediaIds?.find(
    (x) => x.name.toLowerCase() === "instagram"
  );

  const facebook = props.details?.socialMediaIds?.find((x) => x.name.toLowerCase() === "facebook");

  const on360Click = () => {
    history.push(`${RouteKeys.Company360View}/${props.details?._id}`);
  };

  const renderDelete = () => {
    return addressToDelete ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>Are you sure want to delete this address</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={onCloseDeleteConfirm}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={onRemoveAddress}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  return (
    <>
      <div className="d-flex flex-wrap p-4 border-bottom">
        {!editModal.isEditLogo ? (
          <div className=" avatar position-relative mr-2">
            <i onClick={() => setEditModal({ isEditLogo: true })}>
              <CompanyAvatar
                id={props.details?._id}
                className="img-cover img-fluid rounded-circle cursor-pointer "
                alt=""
                title={props.details?.name}
                imageUrl={props.details?.logo?.location}
                image={props.details?.logo}
              ></CompanyAvatar>
            </i>
            {/* <i
            onClick={() => setEditModal({ isEditLogo: true })}
            className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-2 mb-1 contact-edit bg-white"
          ></i> */}
          </div>
        ) : (
          <EditCompanyLogo
            onClose={() => {
              setEditModal({});
            }}
            updateCompany={(details: Company) => {
              props.onStateChange?.();
              props.updateCompany(details);
            }}
            details={props.details}
            property={"logo"}
            value={props.details?.logo}
            title={"Logo"}
          ></EditCompanyLogo>
        )}
        <div className="pl-1 flex-fill position-relative">
          <div className="row no-gutters align-items-center">
            <h4 className="font-weight-semibold mb-1 cursor-pointer" onClick={on360Click}>
              {props.details?.name}{" "}
            </h4>
          </div>
          <div className="row no-gutters align-items-end mb-2">
            <div>
              {props.details?.cinNumber && (
                <div
                  className="font-sm font-weight-semibold mb-1 cursor-pointer"
                  onClick={on360Click}
                >
                  <span className="font-xxs font-weight-normal text-gray-500">CIN Number : </span>
                  {props.details?.cinNumber}
                </div>
              )}
              {props.details?.gstinNumber && (
                <div
                  className="font-sm font-weight-semibold mb-1 cursor-pointer"
                  onClick={on360Click}
                >
                  <span className="font-xxs font-weight-normal text-gray-500">GSTIN Number : </span>
                  {props.details?.gstinNumber}
                </div>
              )}
            </div>
            {props.details?.socialMediaIds && props.details?.socialMediaIds?.length > 0 && (
              <div className="ml-auto font-base">
                {linkedin && (
                  <a href={linkedin.socialId} target="_blank">
                    <i className="icon-linkedin mr-2"></i>
                  </a>
                )}
                {twitter && (
                  <a href={twitter.socialId} target="_blank">
                    <i className="icon-twitter mr-2"></i>
                  </a>
                )}
                {instagram && (
                  <a href={instagram.socialId} target="_blank">
                    <i className="icon-instagram mr-2"></i>
                  </a>
                )}
                {facebook && (
                  <a href={facebook.socialId} target="_blank">
                    <i className="icon-facebook"></i>
                  </a>
                )}
              </div>
            )}
          </div>
          {leadState?.leadDetails?.quality && (
            <div className="pointed-tag bg-primary text-white font-xxs text-uppercase d-inline-block rounded px-2 font-weight-semibold position-relative zIndex-1">
              {convertPascalCaseToWords(leadState?.leadDetails?.quality)}
            </div>
          )}
          {leadState?.leadDetails?.tags && leadState?.leadDetails?.tags?.length > 0 && (
            <div className="d-flex align-items-center py-2 my-1">
              <i className="icon-tag text-gray-600 mr-1"></i>
              {leadState?.leadDetails?.tags?.map((tag) => {
                return (
                  <div className="border rounded font-xxs tag-small d-inline-flex align-items-center px-1 mr-1 mb-1">
                    {tag}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {props.show360Link && (
        <div
          className="d-flex border-bottom align-items-center py-2 px-4 cursor-pointer"
          onClick={on360Click}
        >
          <h6 className="mb-0">Company 360 View</h6>
          <i className="icon-chevron-right icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto"></i>
        </div>
      )}

      {props?.showDetailsLink && (
        <div className="d-flex border-bottom align-items-center py-2 px-4">
          <h6 className="mb-0">Company Details</h6>
          <i
            onClick={() => setShowDetails(!showDetails)}
            className={classnames({
              "  icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto": true,
              "icon-chevron-down": !showDetails,
              "icon-chevron-up": showDetails,
            })}
          ></i>
        </div>
      )}
      {showDetails && (
        <>
          {!editModal.isEditName ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Name</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.name}</div>
                <i
                  onClick={() => setEditModal({ isEditName: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => {
                setEditModal({});
              }}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"name"}
              value={props.details?.name}
              title={"Name"}
            ></EditCompany>
          )}
          {!editModal.isWebsiteUrl ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Website</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.websiteUrl}</div>
                <i
                  onClick={() => setEditModal({ isWebsiteUrl: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"websiteUrl"}
              value={props.details?.websiteUrl}
              title={"Website"}
            ></EditCompany>
          )}
          {!editModal.isEditCinNumber ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">CIN Number</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.cinNumber}</div>
                <i
                  onClick={() => setEditModal({ isEditCinNumber: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"cinNumber"}
              value={props.details?.cinNumber}
              title={"CIN Number"}
            ></EditCompany>
          )}
          {!editModal.isEditGstinNumber ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">GSTIN Number</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.gstinNumber}</div>
                <i
                  onClick={() => setEditModal({ isEditGstinNumber: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"gstinNumber"}
              value={props.details?.gstinNumber}
              title={"GSTIN Number"}
            ></EditCompany>
          )}

          {!editModal.isEditPhone ? (
            <>
              <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
                <div className="w-100"></div>
                <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Phone</div>
                <div className="col pl-2 pr-0 contact-content d-flex align-items-center flex-wrap">
                  {props.details?.phones?.length === 0 && (
                    <i
                      onClick={() => setEditModal({ isEditPhone: true })}
                      className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                    ></i>
                  )}

                  {props.details?.phones?.map((item, index) => {
                    return (
                      <>
                        {item.isPrimary ? (
                          <i className="icon-tick font-xxxs text-shamrock mr-1"></i>
                        ) : (
                          <i
                            className="font-weight-bold font-xxxs icon-close mr-1 text-red"
                            title="not verified"
                          ></i>
                        )}

                        <div className="font-sm font-weight-semibold">
                          +{item.phone}{" "}
                          {item.phoneType && (
                            <span className="text-gray-500">({item.phoneType})</span>
                          )}
                        </div>
                        {item.isPrimary && <i className="icon-primary-contact font-xs ml-1"></i>}
                        {index === 0 && canUpdateCustomerContacts && (
                          <i
                            onClick={() => setEditModal({ isEditPhone: true })}
                            className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                          ></i>
                        )}

                        <div className="w-100"></div>
                      </>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <EditCompanyPhone
              onClose={() => {
                props.onStateChange?.();
                setEditModal({});
              }}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
            ></EditCompanyPhone>
          )}

          {!editModal.isEditEmail ? (
            <>
              <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
                <div className="w-100"></div>
                <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">E-mail</div>
                <div className="col pl-2 pr-0 contact-content d-flex align-items-center flex-wrap">
                  {props.details?.emails?.length === 0 && (
                    <i
                      onClick={() => setEditModal({ isEditEmail: true })}
                      className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                    ></i>
                  )}

                  {props.details?.emails?.map((item, index) => {
                    return (
                      <>
                        {item.isPrimary ? (
                          <i className="icon-tick font-xxxs text-shamrock mr-1"></i>
                        ) : (
                          <i
                            className="font-weight-bold font-xxxs icon-close mr-1 text-red"
                            title="not verified"
                          ></i>
                        )}
                        <div className="font-sm font-weight-semibold">
                          {item.email}{" "}
                          {item.emailType && (
                            <span className="text-gray-500">({item.emailType})</span>
                          )}
                        </div>
                        {item.isPrimary && <i className="icon-primary-contact font-xs ml-1"></i>}
                        {index === 0 && canUpdateCustomerContacts && (
                          <i
                            onClick={() => setEditModal({ isEditEmail: true })}
                            className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                          ></i>
                        )}
                        <div className="w-100"></div>
                      </>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <EditCompanyEmail
              onClose={() => {
                props.onStateChange?.();
                setEditModal({});
              }}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
            ></EditCompanyEmail>
          )}
          {!editModal.isEditSector ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Scale</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.sector?.name}</div>
                <i
                  onClick={() => setEditModal({ isEditSector: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"sector"}
              value={props.details?.sector?.name}
              title={"Scale"}
              loadData={loadSector}
              type={"select"}
            ></EditCompany>
          )}
          {!editModal.isEditIndustry ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Industry</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">{props.details?.industry?.name}</div>
                <i
                  onClick={() => setEditModal({ isEditIndustry: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"industry"}
              value={props.details?.industry?.name}
              title={"Industry"}
              loadData={loadIndustry}
              type={"select"}
            ></EditCompany>
          )}
          {!editModal.isEditCompanySegment ? (
            <div className="mt-2 d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap">
              <div className="w-100"></div>
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">Grade</div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <div className="font-sm font-weight-semibold">
                  {props.details?.companySegment?.name}
                </div>
                <i
                  onClick={() => setEditModal({ isEditCompanySegment: true })}
                  className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
                ></i>
              </div>
              <div className="w-100"></div>
            </div>
          ) : (
            <EditCompany
              onClose={() => setEditModal({})}
              updateCompany={(details: Company) => {
                props.onStateChange?.();
                props.updateCompany(details);
              }}
              details={props.details}
              property={"companySegment"}
              value={props.details?.companySegment?.name}
              title={"Grade"}
              loadData={loadCompanySegment}
              type={"select"}
            ></EditCompany>
          )}

          {!isShowMore ? (
            <div className="d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap bg-white">
              <div className="col pl-0 pr-2 contact-label font-xs text-gray-500"></div>
              <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                <button
                  onClick={() => setShowMore(true)}
                  className="btn btn-link font-xs p-0 font-weight-semibold"
                >
                  Show more
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="contact-label font-sm font-weight-semibold px-4 mt-1 ">Address</div>
              {canViewCustomerAddress && (
                <div className="d-flex flex-column py-1 ">
                  {props.details?.addresses?.map((item) => {
                    return (
                      <div className="contact-form-row ">
                        <div className="contact-content d-flex flex-column ">
                          <div className="d-flex mb-2 flex-column">
                            <div className="d-flex justify-content-end align-items-center ">
                              {item.isPrimary ? (
                                <i className="icon-tick font-xxxs text-shamrock mr-1 pt-1"></i>
                              ) : (
                                <i className="icon-tick font-xxxs text-shamrock mr-1 pt-1 hide-visibility"></i>
                              )}
                              {canUpdateCustomerContacts &&
                                 (
                                  <>
                                    <i
                                      onClick={() => setAddressToDelete(item)}
                                      className="icon-bin-fill cursor-pointer align-self-center font-lg text-gray-700"
                                    ></i>
                                    <i
                                      onClick={() => selectAddress(item)}
                                      className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-3 contact-edit bg-white"
                                    ></i>
                                  </>
                                )}
                            </div>
                            <AddressDetalsView addressDetails={item} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="w-100 mb-3"></div>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500"></div>
                  {!editModal.isShowAddressForm && canUpdateCustomerContacts && (
                    <button
                      onClick={() => selectAddress()}
                      className="btn btn-link font-sm p-0 font-weight-semibold"
                    >
                      + add{" "}
                      {props.details &&
                      props.details.addresses &&
                      props.details.addresses.length > 0
                        ? "one more"
                        : " address"}{" "}
                    </button>
                  )}
                </div>
              )}

              {editModal.isShowAddressForm && (
                <AddCompanyAddress
                  onClose={() => setEditModal({})}
                  updateCompany={(details: Company) => {
                    props.onStateChange?.();
                    props.updateCompany(details);
                  }}
                  selectedAddress={selectedAddress}
                  companyDetails={props.details}
                ></AddCompanyAddress>
              )}
              <div className="d-flex flex-wrap px-4 py-1 contact-form-row align-items-center flex-wrap bg-white">
                <div className="col px-2 contact-label font-xs text-gray-500"></div>
                <div className="col px-2 contact-content d-flex align-items-center">
                  <button
                    onClick={() => setShowMore(false)}
                    className="btn btn-link font-xs p-0 font-weight-semibold"
                  >
                    Show less
                  </button>
                </div>
              </div>
            </>
          )}
          {renderDelete()}
        </>
      )}
    </>
  );
};

export default CompanyDetails;
